/* --------------------------------------------------
// GSAP
// -------------------------------------------------- */
gsap.registerPlugin(ScrollTrigger, SplitText, CustomEase);
CustomEase.create('osmo', '0.625, 0.05, 0, 1');
gsap.defaults({ ease: 'osmo', duration: 0.6 });


/* --------------------------------------------------
// Imports
// -------------------------------------------------- */

// Core
import { lenisScroll } from './modules/lenis.js';
import { anchorScroll } from './modules/anchorScroll.js';
import { themeColors } from './modules/themeColors.js';
import { favicon } from './modules/favicon.js';
import { pageTitleSwitch } from './modules/pageTitleSwitch.js';

// Navigation
import { navigation } from './modules/navigation.js';
import { headerScroll } from './modules/headerScroll.js';

// Typography & Content
import { headingSplit } from './modules/headingSplit.js';
import { wysiwygHeadings } from './modules/wysiwygHeadings.js';
import { vacancyChecklist } from './modules/vacancyChecklist.js';
import { contentRevealScroll } from './modules/contentRevealScroll.js';

// Global Components
import { cursor } from './modules/mouseFollower.js';
import { button } from './modules/button.js';
import { backButton } from './modules/backButton.js';
import { fileUpload } from './modules/fileUpload.js';
import { hoverSound } from './modules/hoverSound.js';
import { marqueeButton } from './modules/marqueeButton.js';
import { accordion } from './modules/accordion.js';
import { ctaConfetti, thankYouConfetti } from './modules/ctaConfetti.js';
import { ctaAvatars } from './modules/ctaAvatars.js';
import { teamCard } from './modules/teamCard.js';
import { serviceCard } from './modules/serviceCard.js';
import { cardsSlider } from './modules/cardsSlider.js';
import { teamSlider } from './modules/teamSlider.js';
import { teamFilter } from './modules/teamFilter.js';
import { mediaSlider } from './modules/mediaSlider.js';
import { autoplayVideos } from './modules/autoplayVideos.js';

// Global Motion
import { parallax } from './modules/parallax.js';
import { bouncer } from './modules/bouncer.js';
import { stackingCards } from './modules/stackingCards.js';

// Projects
import { projectVideo } from './modules/projectVideo.js';
import { projectCardColors } from './modules/projectCardColors.js';
import { projectFilter } from './modules/projectFilter.js';
import { filterChips } from './modules/filterChips.js';
import { projectFilterModal } from './modules/projectFilterModal.js';
import { projectSlider } from './modules/projectSlider.js';

// Homepage
import { frontHero } from './modules/frontHero.js';
import { logoShowcase } from './modules/logoShowcase.js';

// Contact
import { contactMarquee } from './modules/contactMarquee.js';

// Footer
import { footerWatermark } from './modules/footerWatermark.js';
import { backToTop } from './modules/backToTop.js';

// Infinite scroll
import { infiniteScroll } from './modules/infiniteScroll.js';


/* --------------------------------------------------
// Core instances
// -------------------------------------------------- */

const lenis = lenisScroll();


/* --------------------------------------------------
// Once
// -------------------------------------------------- */

function initOnceFunctions() {
    fileUpload();
    anchorScroll(lenis);
    hoverSound();

    favicon();
    pageTitleSwitch();

    document.addEventListener('infinite-scroll:loaded', (event) => {
        event.detail.elements.forEach((element) => {
            projectVideo(element);
            projectCardColors(element);
        });
    });
}


/* --------------------------------------------------
// Page functions
// -------------------------------------------------- */

function initPageFunctions(root = document) {
    themeColors(root);
    const cleanups = [];
    const track = (cleanup) => { if (typeof cleanup === "function") cleanups.push(cleanup); };
    track(autoplayVideos(root));
    const context = gsap.context(() => {
        navigation(root);
        backToTop(lenis, root);
        vacancyChecklist(root);
        headerScroll(root);

        headingSplit(root);
        wysiwygHeadings(root);
        contentRevealScroll(root);

        button(root);
        backButton(root);
        marqueeButton(root);
        accordion(root);

        ctaConfetti(root);
        thankYouConfetti(root);
        ctaAvatars(root);

        teamCard(root);
        serviceCard(root);

        track(cardsSlider(root));
        track(teamSlider(root));
        track(teamFilter(root));
        track(mediaSlider(root));

        track(parallax(root));
        track(bouncer(root));
        stackingCards(root);

        projectVideo(root);
        projectCardColors(root);
        projectFilter(root);
        track(projectFilterModal(root, lenis));
        track(filterChips(root));
        track(projectSlider(root));

        track(frontHero(root));
        track(logoShowcase(root));

        contactMarquee(root);

        footerWatermark(root);

        track(infiniteScroll(root));
    }, root);
    return () => {
        cleanups.forEach((cleanup) => cleanup());
        context.revert();
    };
}


/* --------------------------------------------------
// Init
// -------------------------------------------------- */


initOnceFunctions();

initPageFunctions();

function refreshLayout() {
    lenis.resize();
    ScrollTrigger.refresh();
}

refreshLayout();
// Refresh after images/fonts settle and when the browser restores a cached page.
window.addEventListener('load', refreshLayout, { once: true });
window.addEventListener('pageshow', refreshLayout);
document.fonts.ready.then(refreshLayout);
